import { cancel, put, retry, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
	setComponentLoading,
	setKs4HasBeenChecked,
	setErrorCode,
	setComponentErrors,
} from '../../ks5PerformanceMeasures/singleSchool/slice';
import { getGraphDataForGraphId } from '../../../../../../sharedSelectors';
import { sagaFlowConfigurations } from '../../../../../../constants';
import { getAveragePointsScoreGraphDataBySchoolId } from '../../../../../../api/groupsAPI';
import { getActiveChildSchool, getGroupAccessId } from 'features/app/redux/context';
import { setMainGraphDataSuccess } from '../../ks5PerformanceMeasures/singleSchool/slice';
import { getGroupAccessRecords } from 'features/groups/setup/redux';
import { getKs4HasBeenChecked } from '../../ks5PerformanceMeasures/singleSchool/selectors';
import { translate } from '../../../../../../utils/locale';
/**
 * Get the average points score graph data
 * @returns {SagaIterator<void>}
 */
export function* getAveragePointsScoreGraphFlow(): SagaIterator<void> {
	//get the school id
	const schoolId = yield select(getActiveChildSchool);

	if (!schoolId) {
		//bail out of the saga
		yield cancel();
	}

	//clear the errors for this component
	yield put(setComponentErrors({ component: 'averagePointsScoreGraph', value: undefined }));

	//get the data from the store
	const initialData = yield select(
		getGraphDataForGraphId(
			'matSingleSchoolPerformanceMeasures',
			`0-${schoolId.childSchoolId},ALevelAveragePoints`
		)
	);

	//set the component loading state to true
	yield put(setComponentLoading({ component: 'averagePointsScoreGraph', value: true }));

	//get the group access id
	const groupAccessId = yield select(getGroupAccessId) ?? 0;

	//get the access records (available key stages)
	const accessRecords = yield select(getGroupAccessRecords);

	//check the access record for the the current group access id for KS4
	const isKs4 = accessRecords?.find((x) => x.id === groupAccessId).examLevel === 32;

	//add in additional check for the state of KS4 having already been set
	const ks4HasBeenChecked = yield select(getKs4HasBeenChecked);

	//if the access record is for KS4, check to see if the state of KS4 has already been set
	if (isKs4) {
		//if the state of KS4 has not been set, set it to true
		if (!ks4HasBeenChecked) {
			yield put(setKs4HasBeenChecked(true));
		}

		//set the component loading state to false
		yield put(setComponentLoading({ component: 'averagePointsScoreGraph', value: false }));

		//bail out of the saga
		yield cancel();
	}

	//clear the state of KS4
	yield put(setKs4HasBeenChecked(false));

	//if the group access id is 0, set the errors for this component
	if (groupAccessId === 0) {
		//set the errors for this component
		yield put(
			setComponentErrors({
				component: 'averagePointsScoreGraph',
				value: translate('groups.ks5Pm.common.NO_ACCESS_ID') as string,
			})
		);
		//set the component loading state to false
		yield put(setComponentLoading({ component: 'averagePointsScoreGraph', value: false }));
		//bail out of the saga
		yield cancel();
	}

	//check to see if the data is already in the store
	if (initialData !== undefined) {
		//set the component loading state to false
		yield put(setComponentLoading({ component: 'averagePointsScoreGraph', value: false }));
		//bail out of the saga
		yield cancel();
	}

	//get the data from the api
	const averagePointsScoreGraphData = yield retry(
		sagaFlowConfigurations.apiRetry,
		sagaFlowConfigurations.milliSeconds,
		getAveragePointsScoreGraphDataBySchoolId,
		groupAccessId,
		schoolId.childSchoolId
	);

	//if there is an error, set the errors for this component
	if (averagePointsScoreGraphData.hasError) {
		//if the error code is 204, set the errors for this component
		if (averagePointsScoreGraphData.errors[0].errorCode === 204) {
			yield put(
				setComponentErrors({
					component: 'averagePointsScoreGraph',
					value: ' ',
				})
			);

			//set the errors for this component
			yield put(
				setErrorCode({
					component: 'averagePointsScoreGraph',
					value: 204,
				})
			);
			//set the component loading state to false
			yield put(setComponentLoading({ component: 'averagePointsScoreGraph', value: false }));

			yield cancel();
		}

		//set the errors for this component
		yield put(
			setComponentErrors({
				component: 'averagePointsScoreGraph',
				value: translate('groups.ks5Pm.common.MESSAGE') as string,
			})
		);
		//set the component loading state to false
		yield put(setComponentLoading({ component: 'averagePointsScoreGraph', value: false }));
		//bail out of the saga
		yield cancel();
	}

	//set the data in the store
	yield put(setMainGraphDataSuccess(averagePointsScoreGraphData.responseObject));

	//set the component loading state to true
	yield put(setComponentLoading({ component: 'averagePointsScoreGraph', value: false }));

	//Bail out of the saga
	yield cancel();
}
